import type { CaseInfo } from '../types';

/**
 * Resolves the effective required episode for a given episode.
 *
 * Priority:
 * 1. Explicit requiredEpisodeId on the CaseInfo (override)
 * 2. If episodeNumber > 1 and seasonId is set, find episode N-1 in same season
 * 3. Otherwise, no prerequisite (returns undefined)
 */
export const resolveRequiredEpisode = (
  caseInfo: CaseInfo,
  allCases: CaseInfo[],
): CaseInfo | undefined => {
  if (caseInfo.requiredEpisodeId) {
    const explicit = allCases.find(
      (c) => c.caseId === caseInfo.requiredEpisodeId,
    );
    if (explicit) return explicit;
    // Fall through to sequential check if explicit episode not found in visible cases
  }

  if (
    caseInfo.seasonId &&
    caseInfo.episodeNumber != null &&
    caseInfo.episodeNumber > 1
  ) {
    const prevEpisodeNumber = caseInfo.episodeNumber - 1;
    return allCases.find(
      (c) =>
        c.seasonId === caseInfo.seasonId &&
        c.episodeNumber === prevEpisodeNumber,
    );
  }

  return undefined;
};

/**
 * Convenience wrapper: resolves just the caseId of the required episode.
 */
export const resolveRequiredEpisodeId = (
  caseInfo: CaseInfo,
  allCases: CaseInfo[],
): string | undefined => {
  return resolveRequiredEpisode(caseInfo, allCases)?.caseId;
};
